interface PrivacyPolicyPageProps {
  onBack: () => void
}

const POLICY_SECTIONS = [
  {
    title: '1. Scope',
    paragraphs: [
      'This Privacy Policy explains how Scouta collects, uses, stores, and shares personal information when you use the Scouta website, dashboard, player database, scouting tools, and related services.',
      'It applies to public visitors, registered Scouts, Clubs, administrators, and anyone whose information is submitted through the platform.',
    ],
  },
  {
    title: '2. Information We Collect',
    bullets: [
      'Account details such as name, email address, password credentials managed by our authentication provider, account role, and organization details you submit.',
      'Player profile information such as name, age, positions, club, location, statistics, notes, match records, media links, photos, career history, and visibility settings.',
      'Operational data such as device, browser, IP address, session information, activity logs, and page interactions used to secure and improve the platform.',
      'Communications you send to Scouta, including support requests, onboarding messages, compliance questions, and other direct contact.',
    ],
  },
  {
    title: '3. How We Use Information',
    bullets: [
      'Create and manage user accounts.',
      'Operate the player discovery, scouting, reporting, and club management tools inside Scouta.',
      'Display public player profiles and related football data where a profile has been marked public.',
      'Review, verify, moderate, and administer accounts, clubs, and player records.',
      'Respond to support requests, security issues, legal concerns, and abuse reports.',
      'Analyze platform usage so we can improve performance, reliability, product quality, and user experience.',
    ],
  },
  {
    title: '4. Visibility of Player Profiles',
    paragraphs: [
      'Scouta includes controls that allow certain player records to be made public or private. Public player profiles may be visible on Explore, Top Talents, shared links, and similar discovery surfaces inside the product.',
      'Private player records are intended to remain limited to authorized users and administrators, but users should only upload information they are permitted to share and manage.',
      'Administrators may review or change visibility settings when needed for moderation, compliance, safety, operational support, or platform administration.',
    ],
  },
  {
    title: '5. Legal Bases and Permissions',
    paragraphs: [
      'By using Scouta or submitting player information to Scouta, you represent that you have the right, authority, consent, or other lawful basis required to submit that information and to make it available according to the settings you choose.',
      'If you upload information about a minor, you are responsible for ensuring that all required parent, guardian, club, academy, or other lawful permissions have been obtained before submission.',
    ],
  },
  {
    title: '6. How We Share Information',
    bullets: [
      'With other users and visitors where information is intentionally made public through platform visibility settings.',
      'With Clubs, Scouts, recruiters, and authorized users who are allowed to access the relevant records through Scouta.',
      'With service providers that support hosting, authentication, storage, analytics, communications, moderation, and security.',
      'With legal or regulatory authorities where required by law, court order, valid governmental request, or to protect users, players, Scouta, or the public.',
      'In connection with a merger, acquisition, restructuring, financing, or sale of all or part of the business, subject to appropriate safeguards.',
    ],
    note: 'Scouta does not sell personal data as part of its ordinary product operations.',
  },
  {
    title: '7. Data Retention',
    paragraphs: [
      'We retain information for as long as reasonably necessary to provide the service, maintain records, resolve disputes, enforce our terms, comply with legal obligations, and protect the platform.',
      'Retention periods may vary depending on the type of record, whether the information is public or private, account status, safety concerns, legal obligations, and platform backup schedules.',
    ],
  },
  {
    title: '8. Security',
    paragraphs: [
      'Scouta uses reasonable technical and organizational measures to help protect account and platform data. These measures may include authentication controls, access restrictions, hosted infrastructure safeguards, and administrative review tools.',
      'No online platform can guarantee absolute security. Users are responsible for using strong passwords, controlling access to their devices, and limiting uploads to information they are allowed to share.',
    ],
  },
  {
    title: '9. Your Choices and Rights',
    bullets: [
      'Access or update account information you control through your Scouta account or by contacting us.',
      'Request correction of inaccurate information.',
      'Request deletion of information where applicable, subject to legal, operational, and safety requirements.',
      'Object to or limit certain processing where your local law provides those rights.',
      'Request review of a public/private visibility concern involving a player profile.',
    ],
  },
  {
    title: '10. International Use',
    paragraphs: [
      'Scouta may be accessed from different countries. By using the service, you understand that your information may be processed in jurisdictions where Scouta, its infrastructure providers, or its support operations are located.',
      'Where required, Scouta will use reasonable steps to protect information transferred across borders.',
    ],
  },
  {
    title: '11. Third-Party Services and Links',
    paragraphs: [
      'Scouta may include links to third-party websites, videos, social platforms, payment tools, storage tools, or communication channels. Their privacy practices are governed by their own policies, not this one.',
      'Users should review third-party terms and privacy policies before relying on those services.',
    ],
  },
  {
    title: '12. Changes to This Policy',
    paragraphs: [
      'Scouta may update this Privacy Policy from time to time. When we do, we may revise the effective date, post the updated version in the product, or provide additional notice where appropriate.',
      'Your continued use of Scouta after an update becomes effective means you accept the revised policy.',
    ],
  },
]

export default function PrivacyPolicyPage({ onBack }: PrivacyPolicyPageProps) {
  return (
    <div className="min-h-screen bg-[#050910] text-white">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[30%] h-[30%] bg-green-500/10 blur-[120px] rounded-full" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-blue-500/10 blur-[120px] rounded-full" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 py-20">
          <div className="space-y-6">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-green-400 hover:text-green-300 transition-colors"
            >
              Back to Scouta
            </button>

            <section className="space-y-4">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Legal</p>
              <h1 className="text-4xl md:text-5xl font-display font-bold">Privacy Policy</h1>
              <p className="text-sm uppercase tracking-[0.22em] text-slate-400">Effective Date: May 22, 2026</p>
              <p className="text-lg text-slate-200 max-w-3xl leading-relaxed">
                Scouta is built to help Scouts, Clubs, and football professionals manage player information responsibly.
                This policy explains what information we handle and how we protect and use it.
              </p>
            </section>
          </div>

          <div className="mt-12 rounded-3xl border border-green-500/20 bg-green-500/5 p-6 text-slate-100 leading-relaxed">
            <p>
              If you upload player information to Scouta, you are responsible for making sure you are allowed to do so.
              If you would like a jurisdiction-specific legal review, Scouta should still have this policy reviewed by counsel before production use.
            </p>
          </div>

          <div className="mt-10 grid gap-6">
            {POLICY_SECTIONS.map((section) => (
              <article key={section.title} className="surface-panel rounded-3xl border border-white/10 p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-bold">{section.title}</h2>

                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className="mt-4 text-slate-200 leading-relaxed">
                    {paragraph}
                  </p>
                ))}

                {section.bullets ? (
                  <ul className="mt-4 space-y-3 text-slate-200 leading-relaxed">
                    {section.bullets.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-2 h-2 w-2 rounded-full bg-green-500 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {section.note ? (
                  <p className="mt-4 text-slate-200 leading-relaxed">{section.note}</p>
                ) : null}
              </article>
            ))}

            <article className="surface-panel rounded-3xl border border-white/10 p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold">13. Contact</h2>
              <p className="mt-4 text-slate-200 leading-relaxed">
                For privacy, data, or profile visibility questions, contact Scouta at{' '}
                <a href="mailto:scoutasupport@gmail.com" className="text-green-400 hover:text-green-300">
                  scoutasupport@gmail.com
                </a>
                .
              </p>
            </article>
          </div>
        </div>
      </div>
    </div>
  )
}
